/**
 * ThreadListTabs — threads · mentions · direct, above the thread list
 * (2026-09-17).
 *
 * Miles asked for two ways into the list that Slack has and this did not: the
 * places he has been named, and his one-on-ones. They are a segmented strip that
 * SWAPS the list below rather than a popup over it, so nothing is covered and
 * the list keeps its own scroll.
 *
 * It sits between the header and the search box, ABOVE the box and not below it.
 * `ThreadSearch`'s results panel is in normal flow (`margin-top: 6px`,
 * `max-height: min(52vh, 460px)`), so a strip placed under it would jump down
 * the moment anyone typed — a control that moves while you reach for it. Search
 * stays global across all three tabs.
 *
 * `variant` mirrors `ThreadSearch`'s own prop, and the two mount sites it serves
 * — the fullscreen rail and `ThreadListSheet` — are between them every surface,
 * because the sheet renders both the phone sheet and the docked drop-up panel.
 *
 * Inline, never a portal: no `createPortal`, so it consumes no rung of the
 * overlay ladder and `check-overlay-layers.mjs` has nothing to say about it.
 * Both surfaces are dark, which is what makes `--brand-gold` correct AS TEXT
 * here (the gold-as-text rule is about light grounds).
 *
 * Copy is chrome, in the viewer's language, through `threadListCopy` (D9, and
 * already registered — no new copy module).
 */
import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { threadListCopy } from './threadListCopy';
import type { ThreadListLang } from '../../types/threadCategories';

/** Which list the body shows. `threads` is the list that was always there. */
export type ThreadListTab = 'threads' | 'mentions' | 'direct';

export const THREAD_LIST_TABS: readonly ThreadListTab[] = ['threads', 'mentions', 'direct'];

export interface ThreadListTabsProps {
  active: ThreadListTab;
  onSelect: (tab: ThreadListTab) => void;
  /** Unread mentions. 0 hides the badge; the cap renders as "N+". */
  mentionUnread?: number;
  /** Unread across the viewer's one-on-one threads. */
  directUnread?: number;
  /**
   * The right-click Miles asked for ("they can right click and click read all").
   * Offered on the mentions tab only, and only as a SECOND way in — the visible
   * "mark all read" button in the list's own header is the one that works on a
   * phone, where there is no right button to click.
   */
  onMarkAllRead?: () => void;
  variant?: 'rail' | 'sheet';
  lang?: ThreadListLang;
}

/** Beyond this the badge stops counting and says so. */
export const TAB_BADGE_CAP = 50;

/**
 * What a thread-list surface needs to render the strip and its bodies. ONE
 * bundle, handed identically to the rail and to the sheet, because the three
 * thread surfaces are three renderers of one data source and a prop each of
 * them assembles for itself is how they start disagreeing.
 *
 * `mentionsPanel` is built by the HOST rather than by each surface: the mentions
 * list has its own controller, its own context fetches and its own read state,
 * and two surfaces building it separately would run two of each. The direct tab
 * has no such bundle — its rows are ordinary thread rows, so each surface
 * renders them with its own row anatomy, which is exactly how the existing
 * sections already work.
 */
export interface ThreadListActivityProps {
  tab: ThreadListTab;
  onSelectTab: (tab: ThreadListTab) => void;
  mentionUnread: number;
  directUnread: number;
  onMarkAllRead: () => void;
  mentionsPanel: React.ReactNode;
  lang?: ThreadListLang;
}

const TabsRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
  padding: 2px 12px 8px 12px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  /* The 32px floor is the touch target; the rail's own rows are denser, but a
     control people aim at on a phone is not where that density is spent. */
  min-height: 32px;
  padding: 5px 6px;
  border: 1px solid ${(p) => (p.$active ? 'rgba(198, 167, 94, 0.55)' : 'rgba(198, 167, 94, 0.18)')};
  border-radius: 8px;
  background: ${(p) => (p.$active ? 'rgba(198, 167, 94, 0.16)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--brand-gold)' : 'rgba(255, 255, 255, 0.62)')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11.5px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(198, 167, 94, 0.1);
    color: var(--brand-gold);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }
`;

const TabLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Its own badge rather than `ThreadUnreadBadge`: that one is absolutely
 * positioned for a thread row's corner, and here the count sits inline beside
 * the word so the tab can shrink with it.
 */
const TabBadge = styled.span`
  flex: 0 0 auto;
  min-width: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--brand-gold);
  color: var(--brand-charcoal);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
`;

export function formatTabBadge(n: number): string {
  if (n > TAB_BADGE_CAP) return `${TAB_BADGE_CAP}+`;
  return String(n);
}

export const ThreadListTabs: React.FC<ThreadListTabsProps> = ({
  active,
  onSelect,
  mentionUnread = 0,
  directUnread = 0,
  onMarkAllRead,
  variant = 'rail',
  lang = 'en',
}) => {
  const copy = threadListCopy(lang);
  const rowRef = useRef<HTMLDivElement | null>(null);

  const label: Record<ThreadListTab, string> = {
    threads: copy.tabThreads,
    mentions: copy.tabMentions,
    direct: copy.tabDirect,
  };
  const unread: Record<ThreadListTab, number> = {
    threads: 0,
    mentions: Math.max(0, Math.floor(mentionUnread)),
    direct: Math.max(0, Math.floor(directUnread)),
  };

  // Arrow/Home/End across the strip, the roving-tablist contract every other
  // list control here already honours (the rail's own rows, the search combobox).
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const index = THREAD_LIST_TABS.indexOf(active);
    const last = THREAD_LIST_TABS.length - 1;
    let next = index;
    if (e.key === 'ArrowLeft') next = index <= 0 ? last : index - 1;
    else if (e.key === 'ArrowRight') next = index >= last ? 0 : index + 1;
    else if (e.key === 'Home') next = 0;
    else next = last;
    const target = THREAD_LIST_TABS[next];
    onSelect(target);
    const el = rowRef.current?.querySelector<HTMLButtonElement>(`[data-thread-tab="${target}"]`);
    try { el?.focus(); } catch { /* not mounted yet — selection still moved */ }
  }, [active, onSelect]);

  return (
    <TabsRow
      ref={rowRef}
      role="tablist"
      aria-label={copy.tabsLabel}
      aria-orientation="horizontal"
      data-thread-list-tabs={variant}
      onKeyDown={onKeyDown}
    >
      {THREAD_LIST_TABS.map((tab) => {
        const isActive = tab === active;
        const count = unread[tab];
        return (
          <Tab
            key={tab}
            type="button"
            role="tab"
            id={`thread-list-tab-${tab}`}
            data-thread-tab={tab}
            data-active={isActive ? 'true' : 'false'}
            $active={isActive}
            aria-selected={isActive}
            aria-controls="thread-list-panel"
            // Only the selected tab is in the tab order; the arrows move within.
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(tab)}
            onContextMenu={tab === 'mentions' && onMarkAllRead && count > 0
              ? (e) => { e.preventDefault(); onMarkAllRead(); }
              : undefined}
          >
            <TabLabel>{label[tab]}</TabLabel>
            {count > 0 && (
              <TabBadge data-thread-tab-badge={tab} aria-label={copy.tabUnreadCount(count)}>
                {formatTabBadge(count)}
              </TabBadge>
            )}
          </Tab>
        );
      })}
    </TabsRow>
  );
};

export default ThreadListTabs;
